import type { components } from '@connected/api-client/api.generated';
import type { ZodValidator } from '@tanstack/zod-form-adapter';

import { api } from '@connected/api-client';
import { validators } from '@connected/api-client/generated/validators.generated';
import { usePatcher } from '@connected/api-client/hooks/use-patcher';
import { toPascalCase } from '@connected/api-client/lib/utils';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import FieldInfo from '@connected/form-fields/components/field-info';
import { Label } from '@connected/form-fields/components/label';
import { ClearFields, SingleCheckOptionItem, SingleHorizontalOptionItem, SingleOptionField } from '@connected/form-fields/components/option';
import { Button } from '@connected/ui/components/button';
import { Checkbox } from '@connected/ui/components/checkbox';
import { FileInput } from '@connected/ui/components/file';
import { Input } from '@connected/ui/components/input';
import { DEFAULT_FILE_ORDER } from '@connected/ui/config/file';
import { Section } from '@connected/ui/features/layouts';
import { Container } from '@connected/ui/layouts/container';
import { useForm } from '@tanstack/react-form';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-form-adapter';
import { Loader2, Trash2 } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ElectricalProjectImage } from '@/components/electrical-project-image';
import { ObservationComponent } from '@/components/features/observation';
import { FooterNavigation } from '@/components/navigation/footer-navigation';
import { useElectricalProjectFileDelete } from '@/hooks/use-electrical-project-file-delete';
import { useElectricalProjectFileUpload } from '@/hooks/use-electrical-project-file-upload';
import { useOnline } from '@/hooks/use-online';
import { logger } from '@/lib/logger';
import { queryClient } from '@/lib/query-client';

export const Route = createFileRoute(
    '/(auth)/projects/$projectVersionId/re-inspections/final-circuits/$finalCircuitId/information/',
)({
    component: Page,
    loader: ({ context, params }) => {
        context.queryClient.ensureQueryData(elektraAppQueryOptions.project.inspection.finalCircuit.finalCircuitOptions({
            electricalProjectFinalCircuitId: params.finalCircuitId,
        }));
        context.queryClient.ensureQueryData(elektraAppQueryOptions.project.inspection.finalCircuit.imagesQueryOptions({
            electricalProjectFinalCircuitId: params.finalCircuitId,
            electricalProjectVersionId: params.projectVersionId,
        }));
        context.queryClient.ensureQueryData(elektraAppQueryOptions.project.inspection.distributionBoard.distributionBoardListOptions({
            electricalProjectVersionId: params.projectVersionId,
        }));
    },
});

type VerdelerInformatieFormValues = {
    identifier: string;
    kind: components['schemas']['ElectricalProjectFinalCircuitKind'] | null;
    floorNumber: string;
    spaceNumber: string;
    electricalProjectDistributionBoardId: string | null;
};

function Page() {
    return (
        <>
            <InformationForm />
            <PhotoForm />
        </>
    );
}

// /api/v2/electrical/projects/finalcircuits/edit
function InformationForm() {
    const { t } = useTranslation('re-inspections');

    const params = Route.useParams();

    const { data } = useQuery(
        elektraAppQueryOptions.project.inspection.finalCircuit.finalCircuitOptions({
            electricalProjectFinalCircuitId: params.finalCircuitId,
        }),
    );

    const { data: distributionBoardsData } = useQuery(
        elektraAppQueryOptions.project.inspection.distributionBoard.distributionBoardListOptions({
            electricalProjectVersionId: params.projectVersionId,
        }),
    );

    const { Field, handleSubmit } = useForm<
        VerdelerInformatieFormValues,
        ZodValidator
    >({
        defaultValues: {
            identifier: data?.Circuit?.Identifier || '',
            floorNumber: data?.Circuit?.FloorNumber || '',
            spaceNumber: data?.Circuit?.SpaceNumber || '',
            kind: data?.Circuit?.Kind || null,
            electricalProjectDistributionBoardId: data?.Circuit?.ElectricalProjectDistributionBoardId || null,
        },
        validatorAdapter: zodValidator(),
    });

    async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        await handleSubmit();
    }

    const distributionBoardOptions = useMemo(() => {
        return distributionBoardsData?.DistributionBoards?.map(db => ({
            label: db.Identifier || 'Naamloze verdeelinrichting',
            value: db.ElectricalProjectDistributionBoardId || '',
        })) || [];
    }, [distributionBoardsData]);

    const patcher = usePatcher('/api/v2/electrical/projects/finalcircuits/patch', {
        onMutate: async (variables) => {
            const body = toPascalCase(variables?.body);

            const finalCircuitQueryOptions = elektraAppQueryOptions.project.inspection.finalCircuit.finalCircuitOptions({
                electricalProjectFinalCircuitId: params.finalCircuitId,
            });
            const finalCircuitListQueryOptions = elektraAppQueryOptions.project.inspection.finalCircuit.finalCircuitListOptions({
                electricalProjectVersionId: params.projectVersionId,
            });

            await queryClient.cancelQueries(finalCircuitQueryOptions);
            await queryClient.cancelQueries(finalCircuitListQueryOptions);

            const previousFinalCircuitData = queryClient.getQueryData(finalCircuitQueryOptions.queryKey) as components['schemas']['EditElectricalProjectFinalCircuitQueryResult'];
            const newFinalCircuitData: components['schemas']['EditElectricalProjectFinalCircuitQueryResult'] = {
                ...previousFinalCircuitData,
                Circuit: {
                    ...previousFinalCircuitData.Circuit,
                    ...body,
                    ElectricalProjectFinalCircuitId: params.finalCircuitId,
                },
            };

            const previousFinalCircuitListData = queryClient.getQueryData(finalCircuitListQueryOptions.queryKey) as components['schemas']['ListElectricalProjectFinalCircuitQueryResult'];
            const newFinalCircuitListData: components['schemas']['ListElectricalProjectFinalCircuitQueryResult'] = {
                ...previousFinalCircuitListData,
                FinalCircuits: previousFinalCircuitListData.FinalCircuits.map((fc) => {
                    if (fc.ElectricalProjectFinalCircuitId === params.finalCircuitId) {
                        return {
                            ...fc,
                            ...body,
                        };
                    }
                    return fc;
                }),
            };

            logger.debug({
                message: 'onMutate(finalCircuitQueryOptions)',
                path: '/api/v2/electrical/projects/finalcircuits/patch',
                finalCircuit: {
                    previousData: previousFinalCircuitData,
                    newData: newFinalCircuitData,
                    queryOptions: finalCircuitQueryOptions,
                    body,
                },
                finalCircuitList: {
                    previousData: previousFinalCircuitListData,
                    newData: newFinalCircuitListData,
                    queryOptions: finalCircuitListQueryOptions,
                },
            });

            queryClient.setQueryData(finalCircuitQueryOptions.queryKey, newFinalCircuitData);
            queryClient.setQueryData(finalCircuitListQueryOptions.queryKey, newFinalCircuitListData);

            return { previousFinalCircuitData, finalCircuitQueryOptions };
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries(
                elektraAppQueryOptions.project.inspection.finalCircuit.finalCircuitOptions({
                    electricalProjectFinalCircuitId: params.finalCircuitId,
                }),
            );
            await queryClient.invalidateQueries(
                elektraAppQueryOptions.project.inspection.finalCircuit.finalCircuitListOptions({
                    electricalProjectVersionId: params.projectVersionId,
                }),
            );
        },
        scope: {
            id: params.projectVersionId,
        },
    }, {
        body: {
            ElectricalProjectFinalCircuitId: params.finalCircuitId,
        },
    });

    return (
        <Container>
            <Container.Header>
                <Container.Title>
                    {t('final-circuit.information.navigation.informatie')}
                </Container.Title>
            </Container.Header>
            <Container.Content>
                <form onSubmit={onSubmit} className="flex flex-col gap-4">
                    <Field
                        name="identifier"
                        validators={{
                            onChange:
                        validators.editElectricalProjectFinalCircuitCommand.identifier,
                            onChangeAsync: patcher.patchField('identifier'),
                            onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                        }}
                    >
                        {({ handleChange, state, name }) => (
                            <Label
                                label={t('final-circuit.information.form.name.label')}
                            >
                                <Input
                                    type="text"
                                    name={name}
                                    onChange={e => handleChange(e.target.value)}
                                    value={state.value}
                                    placeholder={t(
                                        'final-circuit.information.form.name.placeholder',
                                    )}
                                />
                                <FieldInfo state={state} />
                            </Label>
                        )}
                    </Field>
                    <Field
                        name="kind"
                        validators={{
                            onChange:
                        validators.editElectricalProjectFinalCircuitCommand.kind,
                            onChangeAsync: patcher.patchField('kind'),
                            onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                        }}
                    >
                        {({ handleChange, state, name }) => (
                            <Label
                                label={t(
                                    'installation.final-circuit.modal.form.type.label',
                                )}
                            >
                                <SingleOptionField
                                    name={name}
                                    handleChange={handleChange}
                                    state={state}
                                    className="flex flex-row"
                                    options={[
                                        { label: t('final-circuit.information.form.type.Machine'), value: 'Machine' },
                                        { label: t('final-circuit.information.form.type.PhotovoltaicInverter'), value: 'PhotovoltaicInverter' },
                                        { label: t('final-circuit.information.form.type.Other'), value: 'Other' },
                                        { label: t('final-circuit.information.form.type.Building'), value: 'Building' },
                                        { label: t('final-circuit.information.form.type.Space'), value: 'Space' },
                                    ]}
                                    render={
                                        SingleHorizontalOptionItem
                                    }
                                />
                            </Label>
                        )}
                    </Field>
                    <Field
                        name="electricalProjectDistributionBoardId"
                        validators={{
                            onChange:
                        validators.editElectricalProjectFinalCircuitCommand.electricalProjectDistributionBoardId,
                            onChangeAsync: patcher.patchField('electricalProjectDistributionBoardId'),
                            onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                        }}
                    >
                        {({ handleChange, state, name }) => (
                            <Label
                                label={t('final-circuit.information.form.parentDistributionBoard.label')}
                                className="relative"
                            >
                                <ClearFields onClear={() => handleChange(null)} />
                                <SingleOptionField
                                    name={name}
                                    handleChange={handleChange}
                                    state={state}
                                    options={distributionBoardOptions}
                                    render={
                                        SingleCheckOptionItem
                                    }
                                    dropdown
                                    placeholder={t('final-circuit.information.form.parentDistributionBoard.placeholder')}
                                    emptyOptionsMessage={t('final-circuit.information.form.parentDistributionBoard.emptyMessage')}
                                    closeOnClick
                                />
                            </Label>
                        )}
                    </Field>
                    <Field
                        name="floorNumber"
                        validators={{
                            onChange:
                        validators.editElectricalProjectFinalCircuitCommand.floorNumber,
                            onChangeAsync: patcher.patchField('floorNumber'),
                            onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                        }}
                    >
                        {({ handleChange, state, name }) => (
                            <Label
                                label={t('final-circuit.information.form.floor.label')}
                            >
                                <Input
                                    type="text"
                                    name={name}
                                    onChange={e => handleChange(e.target.value)}
                                    value={state.value}
                                    placeholder={t(
                                        'final-circuit.information.form.floor.placeholder',
                                    )}
                                />
                                <FieldInfo state={state} />
                            </Label>
                        )}
                    </Field>
                    <Field
                        name="spaceNumber"
                        validators={{
                            onChange:
                        validators.editElectricalProjectFinalCircuitCommand.spaceNumber,
                            onChangeAsync: patcher.patchField('spaceNumber'),
                            onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                        }}
                    >
                        {({ handleChange, state, name }) => (
                            <Label
                                label={t('final-circuit.information.form.room.label')}
                            >
                                <Input
                                    type="text"
                                    name={name}
                                    onChange={e => handleChange(e.target.value)}
                                    value={state.value}
                                    placeholder={t(
                                        'final-circuit.information.form.room.placeholder',
                                    )}
                                />
                                <FieldInfo state={state} />
                            </Label>
                        )}
                    </Field>
                    <ObservationComponent
                        electricalProjectVersionId={params.projectVersionId}
                        objectKind="FinalCircuit"
                        objectId={params.finalCircuitId}
                    />
                </form>
            </Container.Content>
        </Container>
    );
}

function PhotoForm() {
    const { t } = useTranslation('re-inspections');
    const { t: c } = useTranslation('common');

    const params = Route.useParams();

    const { data } = useQuery(
        elektraAppQueryOptions.project.inspection.finalCircuit.finalCircuitOptions({
            electricalProjectFinalCircuitId: params.finalCircuitId,
        }),
    );

    const patcher = usePatcher('/api/v2/electrical/projects/finalcircuits/patch', {
        onMutate: async (variables) => {
            const body = toPascalCase(variables?.body);

            const finalCircuitQueryOptions = elektraAppQueryOptions.project.inspection.finalCircuit.finalCircuitOptions({
                electricalProjectFinalCircuitId: params.finalCircuitId,
            });

            await queryClient.cancelQueries(finalCircuitQueryOptions);

            const previousFinalCircuitData = queryClient.getQueryData(finalCircuitQueryOptions.queryKey) as components['schemas']['EditElectricalProjectFinalCircuitQueryResult'];
            const newFinalCircuitData: components['schemas']['EditElectricalProjectFinalCircuitQueryResult'] = {
                ...previousFinalCircuitData,
                Circuit: {
                    ...previousFinalCircuitData.Circuit,
                    ...body,
                    ElectricalProjectFinalCircuitId: params.finalCircuitId,
                },
            };

            queryClient.setQueryData(finalCircuitQueryOptions.queryKey, newFinalCircuitData);

            return { previousFinalCircuitData, finalCircuitQueryOptions };
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries(
                elektraAppQueryOptions.project.inspection.finalCircuit.finalCircuitOptions({
                    electricalProjectFinalCircuitId: params.finalCircuitId,
                }),
            );
        },
        scope: {
            id: params.projectVersionId,
        },
    }, {
        body: {
            ElectricalProjectFinalCircuitId: params.finalCircuitId,
        },
    });

    return (
        <>
            <Section>
                <Section.Header>
                    <Section.Title>
                        {t('final-circuit.information.navigation.photos-final-circuit')}
                    </Section.Title>
                </Section.Header>
                <Section.Content>
                    <Label label={t('final-circuit.information.form.detailPhotos.label')}>
                        <FinalCircuitFileField type="DetailsImage" />
                    </Label>
                    <Label label={t('final-circuit.information.form.overviewPhotos.label')}>
                        <FinalCircuitFileField type="OverviewImage" />
                    </Label>
                    <Label label={t('final-circuit.information.form.extra-photos.label')}>
                        <FinalCircuitFileField type="ExtraImage" />
                    </Label>
                    <ObservationComponent
                        electricalProjectVersionId={params.projectVersionId}
                        objectKind="FinalCircuitImages"
                        objectId={params.finalCircuitId}
                    />
                </Section.Content>
            </Section>
            <Section>
                <Section.Header className="flex-row justify-between items-center">
                    <Section.Title>
                        {t('final-circuit.information.navigation.photos-thermografie')}
                    </Section.Title>
                    <label className="flex items-center gap-2 text-sm font-normal cursor-pointer">
                        <Checkbox
                            checked={data?.Circuit?.ReportThermalImages !== false}
                            onCheckedChange={(checked) => {
                                patcher.patchField('ReportThermalImages')({ value: checked });
                            }}
                        />
                        {c('report.include-in-report')}
                    </label>
                </Section.Header>
                <Section.Content>
                    <Label label={t('final-circuit.information.form.thermalPhotos.label')}>
                        <FinalCircuitFileField type="ThermalImage" />
                    </Label>
                    <ObservationComponent
                        electricalProjectVersionId={params.projectVersionId}
                        objectKind="FinalCircuitThermalImages"
                        objectId={params.finalCircuitId}
                    />
                </Section.Content>
                <Section.Footer>
                    <FooterNavigation />
                </Section.Footer>
            </Section>
        </>
    );
}

function FinalCircuitFileField({ type }: { type: components['schemas']['ElectricalProjectFinalCircuitFileKind'] }) {
    const params = Route.useParams();
    const isOnline = useOnline();

    const queryOptions = useMemo(() => {
        return elektraAppQueryOptions.files.finalCircuitImage.list({
            electricalProjectVersionId: params.projectVersionId,
            electricalProjectFinalCircuitId: params.finalCircuitId,
        });
    }, [params.projectVersionId, params.finalCircuitId]);

    const { data: fileList } = useQuery(queryOptions);

    const fileDelete = useElectricalProjectFileDelete({
        mutationOptions: {
            scope: {
                id: params.projectVersionId,
            },
        },
    });

    const linkMutation = api.useMutation(
        'post',
        '/api/v2/electrical/projects/finalcircuits/files/create',
        {
            onSettled: () => {
                queryClient.invalidateQueries(
                    elektraAppQueryOptions.project.inspection.finalCircuit.finalCircuitOptions({
                        electricalProjectFinalCircuitId: params.finalCircuitId,
                    }),
                );
                queryClient.invalidateQueries(queryOptions);
            },
            scope: {
                id: params.projectVersionId,
            },
        },
    );

    const unlinkMutation = api.useMutation(
        'delete',
        '/api/v2/electrical/projects/finalcircuits/files/delete',
        {
            onMutate: async (variables) => {
                const { params: mutationParams } = variables;

                const electricalProjectFileId = fileList?.Files?.find((file: any) => file.ElectricalProjectFinalCircuitFileId === mutationParams.query.ElectricalProjectFinalCircuitFileId)?.ElectricalProjectFileId;

                if (!electricalProjectFileId) {
                    logger.warn({
                        message: 'onMutate(unlinkMutation)',
                        description: 'ElectricalProjectFileId not found',
                        variables,
                    });

                    return;
                }

                fileDelete.delete(electricalProjectFileId);
            },
            onSettled: () => {
                queryClient.invalidateQueries(
                    elektraAppQueryOptions.project.inspection.finalCircuit.finalCircuitOptions({
                        electricalProjectFinalCircuitId: params.finalCircuitId,
                    }),
                );
                queryClient.invalidateQueries(queryOptions);
            },
            scope: {
                id: params.projectVersionId,
            },
        },
    );

    const fileUpload = useElectricalProjectFileUpload({
        endpoint: '/api/v2/electrical/projects/files/upload/finalcircuit',
        mutationOptions: {
            onMutate: async (file) => {
                const electricalProjectFinalCircuitFileId = crypto.randomUUID();

                queryClient.cancelQueries(queryOptions);

                linkMutation.mutate({
                    body: {
                        ElectricalProjectFileId: file.ElectricalProjectFileId!,
                        ElectricalProjectFinalCircuitId: params.finalCircuitId,
                        Kind: type,
                        Order: DEFAULT_FILE_ORDER,
                        // TODO: Add back when API is updated
                        // ElectricalProjectFinalCircuitFileId: electricalProjectFinalCircuitFileId,
                    },
                });

                const currentData = queryClient.getQueryData(queryOptions.queryKey) as components['schemas']['ListElectricalProjectFinalCircuitFileQueryResult'];

                const newData: components['schemas']['ListElectricalProjectFinalCircuitFileQueryResult'] = {
                    Count: (currentData?.Count ?? 0) + 1,
                    Files: [
                        ...(currentData?.Files || []),
                        {
                            ElectricalProjectFinalCircuitFileId: electricalProjectFinalCircuitFileId,
                            ElectricalProjectFileId: file.ElectricalProjectFileId!,
                            ElectricalProjectFinalCircuitId: params.finalCircuitId,
                            Kind: type,
                            Order: DEFAULT_FILE_ORDER,
                            IsDeleted: false,
                            LastUpdateDate: new Date().toISOString(),
                        },
                    ],
                };

                queryClient.setQueryData(queryOptions.queryKey, newData);
            },
            scope: {
                id: params.projectVersionId,
            },
        },
    });

    function handleChange(fileList: FileList) {
        const file = fileList[0];

        if (!file)
            return;

        fileUpload.upload({
            file,
        });
    }

    return (
        <>
            <FileInput
                type="image"
                accept={fileUpload.accept ?? undefined}
                onChange={handleChange}
                disabled={isOnline && fileUpload.isDisabled}
                mode="both"
            />
            {fileList?.Files?.filter((file: any) => file.Kind === type).map((file: any) => (
                <div key={file.ElectricalProjectFinalCircuitFileId!} className="relative flex flex-row gap-2">
                    <ElectricalProjectImage
                        className="h-[112px] w-full rounded-lg"
                        query={{
                            ElectricalProjectFileId: file.ElectricalProjectFileId!,
                        }}
                        alt={file.FileName ?? ''}
                        expandable
                    />
                    <Button
                        className="absolute top-1 right-1 bg-background/50"
                        variant="ghost"
                        size="icon"
                        onClick={() => unlinkMutation.mutate({
                            params: {
                                query: {
                                    ElectricalProjectFinalCircuitFileId: file?.ElectricalProjectFinalCircuitFileId ?? '',
                                },
                            },
                        })}
                        disabled={fileDelete.isDisabled || !file?.ElectricalProjectFinalCircuitFileId || unlinkMutation.isPending}
                    >
                        {unlinkMutation.isPending ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                    </Button>
                </div>
            ))}
        </>
    );
}
